import React, { useEffect, useState } from 'react';
import { SortingRule } from 'react-table';
import Toast from '../../helpers/Toast';
import { Utilities } from '../../helpers/Utilities';
import { AssistanceServiceTripsDTO } from '../../models/AssistanceServiceTrips/AssistanceServiceTripsDTO';
import { AssistanceServiceTripsService } from '../../services/AssistanceServiceTripsService';
import TableComponent from '../TableComponent';
import { FormattedMessage, useIntl } from 'react-intl';
import ModalComponent from '../ModalComponent';
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import { Col, Row } from 'reactstrap';
import { TotalDebtKmDTO } from '../../models/AssistanceServiceTrips/TotalDebtKmDTO';

type AssistanceServiceTripsTableProps = {
    assistanceId: number;
    reload: boolean;
    totalDebtkmInAssistance: number;
    onDebtKmUpdated?: () => void;
}


function AssistanceServiceTripsTable({ assistanceId, reload, totalDebtkmInAssistance, onDebtKmUpdated }: AssistanceServiceTripsTableProps) {
    const assistanceServiceTripsService = new AssistanceServiceTripsService();
    const [data, setData] = useState<AssistanceServiceTripsDTO[]>([]);
    const pageSizes: number[] = [5, 10];
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes, 5));
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [goToPage, setGoToPage] = useState<number>();
    const [pageCount, setPageCount] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);

    const intl = useIntl();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [removeLineId, setRemoveLineId] = useState<number | null>(null);
    const [reloadAfterRemove, setReloadAfterRemove] = useState<boolean>(false);

    const [isCreateButtonLoading, setIsCreateButtonLoading] = useState<boolean>(false);
    const [kmData, setkmData] = useState<TotalDebtKmDTO>();

    const columns = React.useMemo(
        () => [
            {
                Header: <FormattedMessage id="Screens.AssistanceServiceTripsTable.Table.OriginLocal" />,
                accessor: "originTypeName",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.AssistanceServiceTripsTable.Table.OriginAddress" />,
                accessor: "originAddress",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.AssistanceServiceTripsTable.Table.OriginDate" />,
                accessor: "originDate",
                width: 10,
                Cell: ({ value }: { value: string | Date }) => (
                    <span>{Utilities.formatDateTimeDDMMYYYYHHMM(value)}</span>
                ),
            },
            {
                Header: <FormattedMessage id="Screens.AssistanceServiceTripsTable.Table.DestinationLocal" />,
                accessor: "destinationTypeName",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.AssistanceServiceTripsTable.Table.DestinationAddress" />,
                accessor: "destinationAddress",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.AssistanceServiceTripsTable.Table.DestinationDate" />,
                accessor: "destinationDate",
                width: 10,
                Cell: ({ value }: { value: string | Date }) => (
                    <span>{Utilities.formatDateTimeDDMMYYYYHHMM(value)}</span>
                ),
            },
            {
                Header: <FormattedMessage id="Screens.AssistanceServiceTripsTable.Table.Km" />,
                accessor: "totalKm",
                width: 5,
            },
            {
                Header: "",
                accessor: " ",
                width: 30,
                Cell: (props: any) => {
                    return (
                        <div style={{ display: "inline-flex", height: "30px" }}>
                            <ButtonComponent
                                text={intl.formatMessage({ id: "ExternalAssistancesTableComponent.PopUp.Remove" })}
                                onClick={() => {
                                    setIsOpen(true);
                                    setRemoveLineId(props.cell.row.original.id)
                                }} />
                        </div>
                    )
                }
            },
        ],
        [],
    );

    const fetchData = async (page: number, pageSize: number, sortBy: SortingRule<object>[] | null) => {
        var searchUrl: string = Utilities.CreateSearchURL([
            `page=${page}`,
            `pageSize=${pageSize}`,
        ]);

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue("sortField", "string", sortBy[0].id)}${Utilities.AddParamToQueryIfValue(
                "sortOrder", "string", sortBy[0].desc === true ? "DESC" : "ASC",
            )}`;
        }

        setIsLoading(true);
        var response = await assistanceServiceTripsService.GetAll(
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize,
            assistanceId
        );

        if (response.success !== true) {
            setCurrentPage(1);
            setGoToPage(1);
            setData([]);
            setPageCount(0);
            setIsLoading(false);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }
        if (response.items.length === 0) {
            setCurrentPage(1);
            setGoToPage(1);
        }
        setData(response.items);
        setPageCount(response.totalPages);
        setIsLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0)

        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    const Remove = async (id: number | null) => {
        if (id == null) return;
        const response = await assistanceServiceTripsService.RemoveServiceTripFromAssistance(id);
        if (response.success === false) {
            Toast.Show("error", response.message);
            return;
        }

        setReloadAfterRemove(!reloadAfterRemove);
        Toast.Show("success", "Deslocação removida da assistência com sucesso");
    };

    const InsertTotalDebtKm = async () => {
        if (kmData == null || isNaN(kmData.totalDebtkm ?? 0)) {
            Toast.Show("error", "Distância a faturar inválida");
            return;
        }

        setIsCreateButtonLoading(true);
        const data: TotalDebtKmDTO = {
            ...kmData,
            idAssistance: assistanceId,
        };

        const response = await assistanceServiceTripsService.InsertTotalDebtKm(data);
        setIsCreateButtonLoading(false);

        if (response.success === false) {
            Toast.Show("error", response.message);
            return;
        }

        setkmData({
            idAssistance: assistanceId,
            totalDebtkm: kmData.totalDebtkm
        });

        Toast.Show("success", "Distância a faturar debitada com sucesso");
    };

    useEffect(() => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    }, [reload, reloadAfterRemove]);

    useEffect(() => {
        if (!kmData) {
            setkmData({
                idAssistance: assistanceId,
                totalDebtkm: totalDebtkmInAssistance
            });
        }
    }, [assistanceId, totalDebtkmInAssistance, kmData]);

    return (
        <div>
            <ModalComponent
                IsOpen={isOpen}
                text={`Tem a certeza que quer remover a deslocação da assistência?`}
                OnClickRight={() => setIsOpen(false)}
                OnClickLeft={() => { setIsOpen(false); Remove(removeLineId) }}
                title={"Remover deslocação da assistência"}
                buttonRight={"Não"}
                buttonLeft={"Sim"}
            />

            <TableComponent
                columns={columns}
                data={data}
                goToPage={goToPage}
                initialPage={page}
                pageCount={pageCount}
                initialPageSize={pageSize}
                pageSizes={pageSizes}
                manualPagination={true}
                loading={isLoading}
                fetchData={fetchData}
                initialSortBy={initialSortBy}
                disableSortBy={false}
            />

            <Row
                style={{
                    margin: "0px",
                    marginTop: "40px",
                    padding: "0px",
                    alignItems: "flex-end",
                    alignContent: "flex-end",
                }}
            >
                <InputComponent
                    xl={4}
                    lg={4}
                    md={4}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    type={"number"}
                    value={data.reduce((acc, cur) => acc + (cur.totalKm ?? 0), 0)}
                    label={intl.formatMessage({ id: "Screens.Assistances.AditionalInfo.Label.TotalKmRegisted" })}
                    readOnly={true}
                />
                <InputComponent
                    xl={4}
                    lg={4}
                    md={4}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    type={"number"}
                    value={kmData?.totalDebtkm ?? ""}
                    label={intl.formatMessage({ id: "Screens.Assistances.AditionalInfo.Label.TotalDebtKm" })}
                    readOnly={isCreateButtonLoading}
                    onChange={(value: number) => {
                        setkmData(prev => ({
                            ...prev,
                            idAssistance: assistanceId,
                            totalDebtkm: value
                        }));
                    }}
                />


                <Col xl={4} lg={4} md={4} sm={12} xs={12}>
                    <ButtonComponent
                        style={{ width: "100%", marginBottom: "8px" }}
                        text={intl.formatMessage({ id: "AssistanceServiceTripsComponent.InsertTotalDebtKm" })}
                        onClick={() => InsertTotalDebtKm()}
                        disabled={isCreateButtonLoading}
                    />
                </Col>
            </Row>
        </div>
    )
}

export default AssistanceServiceTripsTable